'use client'

import BlogThumbnail from './BlogThumbnail'
import BlogHero from './BlogHero'
import FilterableContainer from '@/components/content/common/FilterableContainer'
import { PageContextProvider } from '@/components/content/common/PageContext'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import type { App, Category, Blog } from '@/payload-types'
import type { FeaturedSection } from '@/types/app'
import { createLogger } from '@/lib/logger'

const logger = createLogger('BlogContainer')

type BlogPost = Pick<
  Blog,
  'id' | 'name' | 'slug' | 'summary' | 'blogApps' | 'blogCategories' | 'createdAt'
>
type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug' | 'icon' | 'appRecommendations'>

type BlogContainerProps = {
  blogs: BlogPost[]
  blogFeaturedTagSections: FeaturedSection[]
  categories: CategoryFilter[]
  apps: App[]
}

export function filterBlogs({
  blogItems,
  categoryId,
}: {
  blogItems: BlogPost[]
  categoryId: Category['id']
}) {
  return blogItems.filter((item) =>
    item.blogCategories?.docs?.some((blogCategory) => {
      if (typeof blogCategory === 'number') {
        logger.error('Blog category is a number instead of an object with a blog', { blogCategory })
        return false
      }
      const category = blogCategory.category

      return typeof category === 'number' ? category === categoryId : category.id === categoryId
    }),
  )
}

export default function BlogContainer({
  blogs,
  blogFeaturedTagSections,
  categories,
  apps,
}: BlogContainerProps) {
  return (
    <PageContextProvider scrollOffset={145}>
      <BlogHero apps={apps} type="root" />
      <FilterableContainer
        collection="blogs"
        categories={categories}
        items={blogs}
        featuredItems={blogFeaturedTagSections}
        element={({ item, type }: { item: BlogPost; type?: string }) => (
          <BlogThumbnail blog={item} type={type} />
        )}
        listElement={({ item }: { item: BlogPost }) => (
          <BlogThumbnail blog={item} type={FeaturedTagType.ThumbnailPrimary} />
        )}
        filterItems={(items, filter) =>
          filterBlogs({
            blogItems: items,
            categoryId: filter.id,
          })
        }
        overlay={false}
      />
    </PageContextProvider>
  )
}
